import * as React from 'react'
import type { GraphResponse } from '@axilon-apps/domains-ontology-ui-state'
import {
  NODE_TYPE_COLORS,
  UNRECOGNIZED_NODE_COLOR,
  colorForNodeLabel
} from '@axilon-apps/domains-ontology-ui-state'

interface GraphLegendProps {
  graph: GraphResponse
}

interface LegendEntry {
  label: string
  color: string
}

/**
 * Color legend for the graph canvas. Lists only the node types actually present
 * in the current graph (so it stays short), each with the per-type palette color
 * from `NODE_TYPE_COLORS`. If any node carries a label outside the published
 * ontology, a single gray "Other" entry is appended to mirror the canvas
 * fallback color.
 */
export default function GraphLegend({ graph }: GraphLegendProps) {
  const entries = React.useMemo<LegendEntry[]>(() => {
    const recognized = new Set<string>()
    let hasUnrecognized = false

    for (const node of graph.nodes) {
      const label = node.label
      if (!label) continue
      if (label in NODE_TYPE_COLORS) {
        recognized.add(label)
      } else {
        hasUnrecognized = true
      }
    }

    const result: LegendEntry[] = Array.from(recognized)
      .sort((a, b) => a.localeCompare(b))
      .map((label) => ({ label, color: colorForNodeLabel(label) }))

    if (hasUnrecognized) {
      result.push({ label: 'Other', color: UNRECOGNIZED_NODE_COLOR })
    }

    return result
  }, [graph])

  if (entries.length === 0) return null

  return (
    <div
      className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md bg-bg-sunk px-3 py-2"
      aria-label="Node type color legend"
    >
      <span className="text-xs font-semibold text-ink-2">Node types</span>
      {entries.map((entry) => (
        <span key={entry.label} className="flex flex-row items-center gap-1.5">
          <span
            className="inline-block size-3 shrink-0 rounded-full border border-line"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-ink-3">{entry.label}</span>
        </span>
      ))}
    </div>
  )
}
